import { Component, ElementRef } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { SupportCategory } from 'SupportApp/interfaces/support-category.interface';
import { SupportCategoryResponse } from 'SupportApp/interfaces/support-category-response.interface';
import { SupportContent } from 'SupportApp/interfaces/support-content.interface';

import { SupportService } from 'SupportApp/services/support.service';

@Component({
    standalone: false,
    selector: 'ycn-support-category-page',
    templateUrl: './support-category-page.template.html',
})
export class SupportCategoryPageComponent {
    pageName!: string;

    loading: boolean = true;

    categories: SupportCategory[] = [];

    contactCategory?: SupportCategory;

    activeContentId!: number;

    activeCategory!: SupportCategory;

    activeContentList: SupportContent[] = [];

    constructor(private supportService: SupportService, private eltRef: ElementRef, private sanitizer: DomSanitizer) {
        this.pageName = this.eltRef.nativeElement.getAttribute('pageName');
        // prevent browsers previous scroll position on refresh to favour hash positiion
        window.history.scrollRestoration = 'manual';
    }

    ngOnInit(): void {
        this.fetchCategory();
    }

    updateActiveCategoryOnClick(category: SupportCategory): void {
        this.activeCategory = category;
        this.getCategoryClosestContent();
        this.updateActiveContentList();
        if (this.activeCategory.content.length > 0) {
            this.activeContentId = this.activeCategory.content[0].id;
        }
        window.scrollTo(0, 0);
    }

    updateActiveContent(contentId?: number): void {
        if (this.activeContentList.length === 0) {
            return;
        }
        const newContentId = contentId || this.activeContentList[0].id;

        if (this.activeContentId === newContentId) {
            this.activeContentId = -1;
        } else {
            this.activeContentId = newContentId;
        }
    }

    private getCategoryClosestContent(): void {
        if (this.activeCategory) {
            if (this.activeCategory.content.length === 0 && this.activeCategory.sub_categories.length > 0) {
                this.activeCategory = { ...this.activeCategory.sub_categories[0] };
            }
        }
    }

    private updateActiveContentList(): void {
        if (!this.activeCategory) {
            return;
        }
        this.activeContentList = this.activeCategory.content.map((content) => ({
            ...content,
            sanitized_body: this.sanitizer.bypassSecurityTrustHtml(content.body),
        }));
    }

    private fetchCategory(): void {
        this.supportService.getPageCategories(this.pageName).subscribe((responseData: SupportCategoryResponse) => {
            this.pageName = responseData.page;

            this.categories = responseData.categories.map((category: SupportCategory) =>
                this.prepareCategory(category)
            );

            // Contact Information category needs unique content.
            this.contactCategory = responseData.categories.find(
                (category: SupportCategory) => category.name === 'Contact Information'
            );
            const hash = decodeURIComponent(window.location.hash.replace('#', ''));
            const foundHasnInCategoryOrContent = hash ? this.initializeFromCategoryLocationHash(hash) : false;
            if (!foundHasnInCategoryOrContent) {
                this.initializeFromEmptyHash();
            }
            this.loading = false;
        });
    }

    private initializeFromCategoryLocationHash(hash: string): boolean {
        this.activeContentId = -1;
        const foundCategory = this.searchCategoryByHash(hash, this.categories);
        if (foundCategory) {
            this.activeCategory = foundCategory;
            this.getCategoryClosestContent();
            this.updateActiveContentList();
            if (this.activeCategory.content.length > 0 && this.activeContentId === -1) {
                this.activeContentId = this.activeCategory.content[0].id;
            }
            setTimeout(() => {
                const elToScrollTo = document.getElementById(hash);
                if (elToScrollTo) {
                    elToScrollTo.scrollIntoView();
                }
            });

            return true;
        }
        return false;
    }

    private initializeFromEmptyHash(): void {
        [this.activeCategory] = this.categories;
        this.getCategoryClosestContent();
        this.updateActiveContentList();
        if (this.activeContentList.length > 0) {
            this.activeContentId = this.activeContentList[0].id;
        }
    }

    private searchCategoryByHash(hash: string, categories: SupportCategory[]): SupportCategory | undefined {
        let foundCategory;
        for (let i = 0; i < categories.length; i += 1) {
            const category = categories[i];
            if (category.internal_name === hash) {
                foundCategory = category;
                break;
            }
            const foundHashInContent = category.content.find((content) => content.slug === hash);
            if (foundHashInContent) {
                this.activeContentId = foundHashInContent.id;
                foundCategory = category;
                break;
            }
            foundCategory = this.searchCategoryByHash(hash, category.sub_categories);
            if (foundCategory) {
                break;
            }
        }
        return foundCategory;
    }

    private prepareCategory(category: SupportCategory): SupportCategory {
        return {
            ...category,
            sanitized_upsell_html: this.sanitizer.bypassSecurityTrustHtml(category.upsell_html),
            sub_categories: category.sub_categories.map((sub_category) => this.prepareCategory(sub_category)),
        };
    }
}
